// =============================================================================
// MetaClient.ts — Meta-progressão no cliente (#90/#94), em dois regimes:
// - LOGADO (persisted:true): o perfil mora no Postgres e toda operação vai
//   pela API HTTP (GET /api/perfil, POST /api/gabinete/*), validada no servidor.
// - ANÔNIMO/modo dev (persisted:false): o perfil mora no localStorage DESTE
//   navegador, como pedem as issues #90/#94 — sobrevive a restart/deploy do
//   servidor. Compra e respec são resolvidos aqui com as MESMAS regras
//   (shared/meta.ts); o crédito de fim de partida chega pelo socket
//   (MSG.META_CREDIT) e é somado aqui. Só afeta o próprio bolso: o servidor
//   sanitiza o estado no join e continua mandando na partida em si.
// =============================================================================
import {
  META_UPGRADE_TABLE, VOTOS_BOCA_URNA, freshMetaProfile, metaTotalSpent, metaUpgradeCost,
  sanitizeMetaProfile,
  type GabineteResponse, type MetaProfile, type PerfilResponse, type PlayerMetaState,
} from '../../shared/meta';
import {
  anistiaArmas, creditMatchToFeitos, freshFeitosState, sanitizeFeitosState,
  type FeitosCreditResult, type FeitosState, type MatchFeats,
} from '../../shared/feitos';

const STORAGE_KEY = 'bs_meta';
const FEITOS_KEY = 'bs_feitos';

// -----------------------------------------------------------------------------
// Perfil local (anônimo/modo dev): localStorage do navegador
// -----------------------------------------------------------------------------
export function loadLocalProfile(): MetaProfile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? sanitizeMetaProfile(JSON.parse(raw)) : freshMetaProfile();
  } catch {
    return freshMetaProfile();   // storage bloqueado/corrompido: conta nova
  }
}

function saveLocalProfile(profile: MetaProfile): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch { /* modo anônimo do navegador sem cota: paciência */ }
}

// Crédito de fim de partida do anônimo (MSG.META_CREDIT): soma e guarda
export function creditLocalProfile(votos: number, caixa2: number): MetaProfile {
  const p = loadLocalProfile();
  p.votos += Math.max(0, Math.floor(Number(votos) || 0));
  p.caixa2 += Math.max(0, Math.floor(Number(caixa2) || 0));
  saveLocalProfile(p);
  return p;
}

// -----------------------------------------------------------------------------
// Feitos locais (#99/#92/#95): estatísticas, conquistas e dossiês do anônimo
// moram no bs_feitos deste navegador (mesmo regime do bs_meta)
// -----------------------------------------------------------------------------
export function loadLocalFeitos(): FeitosState {
  try {
    const raw = localStorage.getItem(FEITOS_KEY);
    if (raw) return sanitizeFeitosState(JSON.parse(raw));
    // Primeira visita das travas por conquista: quem JÁ jogava antes (perfil
    // local com votos além da boca de urna) não regride de conteúdo — as
    // conquistas que travam arma nascem anistiadas (migração, issue #99)
    const estado = freshFeitosState();
    if (loadLocalProfile().votos > VOTOS_BOCA_URNA) anistiaArmas(estado);
    saveLocalFeitos(estado);
    return estado;
  } catch {
    return freshFeitosState();   // storage bloqueado/corrompido: ficha limpa
  }
}

function saveLocalFeitos(estado: FeitosState): void {
  try {
    localStorage.setItem(FEITOS_KEY, JSON.stringify(estado));
  } catch { /* modo anônimo do navegador sem cota: paciência */ }
}

// Crédito de feitos do anônimo (MSG.META_CREDIT): funde a participação nas
// MESMAS funções puras do servidor; o bônus de conquistas/dossiês em votos
// vitalícios entra direto no perfil local (bs_meta)
export function creditLocalFeitos(feitos: MatchFeats): { estado: FeitosState; profile: MetaProfile; result: FeitosCreditResult } {
  const estado = loadLocalFeitos();
  const result = creditMatchToFeitos(estado, feitos);
  saveLocalFeitos(estado);
  const profile = result.votosBonus > 0
    ? creditLocalProfile(result.votosBonus, 0)
    : loadLocalProfile();
  return { estado, profile, result };
}

// Estado que viaja no room:create/room:join: pra quem é logado o servidor
// ignora e usa o Postgres; pro anônimo é a fonte dos bônus e desbloqueios
export function metaJoinState(): PlayerMetaState {
  const p = loadLocalProfile();
  return { votos: p.votos, upgrades: p.upgrades, feitos: loadLocalFeitos() };
}

// -----------------------------------------------------------------------------
// Perfil (API pra logado; localStorage pra anônimo)
// -----------------------------------------------------------------------------
export async function fetchPerfil(): Promise<PerfilResponse> {
  try {
    const res = await fetch('/api/perfil');
    if (!res.ok) throw new Error(String(res.status));
    const data = (await res.json()) as PerfilResponse;
    if (!data.ok) return data;
    if (data.persisted && data.profile) {
      return { ...data, feitos: sanitizeFeitosState(data.feitos) };
    }
    // Sem sessão (ou servidor sem banco): perfil e feitos são os do navegador
    return { ok: true, persisted: false, profile: loadLocalProfile(), feitos: loadLocalFeitos() };
  } catch {
    return { ok: false, persisted: false, profile: null, feitos: null };
  }
}

// -----------------------------------------------------------------------------
// Gabinete de Campanha: compra e Reforma Ministerial
// -----------------------------------------------------------------------------
async function post(url: string, body: unknown): Promise<GabineteResponse> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return (await res.json()) as GabineteResponse;
  } catch {
    return { ok: false, error: 'Sem linha com o comitê. Confira a conexão.' };
  }
}

// Compra local (anônimo): mesmas regras do servidor, via shared/meta.ts
function comprarLocal(itemId: string): GabineteResponse {
  const def = META_UPGRADE_TABLE[itemId];
  if (!def) return { ok: false, error: 'Upgrade não consta no orçamento.' };
  const p = loadLocalProfile();
  const level = p.upgrades[def.id] ?? 0;
  if (level >= def.max) return { ok: false, error: 'Nível máximo: o TCU barrou o aditivo.', profile: p };
  const custo = metaUpgradeCost(def, level);
  if (p.caixa2 < custo) return { ok: false, error: 'Caixa 2 insuficiente. Volte pra rua (de novo).', profile: p };
  p.caixa2 -= custo;
  p.upgrades[def.id] = level + 1;
  saveLocalProfile(p);
  return { ok: true, profile: p };
}

function respecLocal(): GabineteResponse {
  const p = loadLocalProfile();
  p.caixa2 += metaTotalSpent(p.upgrades);
  p.upgrades = {};
  saveLocalProfile(p);
  return { ok: true, profile: p };
}

export function comprarUpgrade(persisted: boolean, item: string): Promise<GabineteResponse> {
  return persisted ? post('/api/gabinete/comprar', { item }) : Promise.resolve(comprarLocal(item));
}

export function reformaMinisterial(persisted: boolean): Promise<GabineteResponse> {
  return persisted ? post('/api/gabinete/respec', {}) : Promise.resolve(respecLocal());
}
